import React, { useState } from 'react';
import TemplateTextField from './TemplateTextField';

const StepWarnings = ({ formData, updateFormData, prevStep, disabled, adviserProfile, onUseAdviserProfile, onSaveAdviserProfile }) => {
  const data = formData.warnings;
  const [savingProfile, setSavingProfile] = useState(false);

  const handleChange = (field, value) => {
    updateFormData('warnings', { [field]: value });
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await onSaveAdviserProfile();
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <fieldset disabled={disabled}>
      <h4 className="soa-step-title">Warnings, Disclaimers &amp; Adviser Details</h4>
      <p className="soa-step-subtitle">Compliance warnings, alternatives considered, and adviser details</p>

      {/* Pre-populated warnings */}
      <div className="alert alert-warning" style={{ fontSize: '0.85rem' }}>
        <i className="bi bi-info-circle me-1"></i>
        Standard general advice warnings and market risk disclaimers are automatically included in the PDF.
        Use the fields below to add any <strong>additional</strong> specific warnings.
      </div>

      <div className="soa-section-banner">
        <i className="bi bi-exclamation-triangle"></i>
        Additional Warnings
      </div>

      <TemplateTextField
        fieldKey="warnings.specificWarnings"
        label="Specific Warnings"
        value={data.specificWarnings}
        onChange={(val) => handleChange('specificWarnings', val)}
        placeholder="e.g. This recommendation involves switching from your current fund which may trigger capital gains tax. You should consider the impact of any exit fees or loss of insurance cover..."
        rows={3}
        disabled={disabled}
      />

      <div className="soa-form-group">
        <label>Product-Specific Risks</label>
        <textarea
          className="form-control"
          value={data.productRisks}
          onChange={(e) => handleChange('productRisks', e.target.value)}
          placeholder="e.g. The recommended portfolio includes exposure to international equities which carry currency risk. Managed fund investments may have limited liquidity..."
          rows={3}
        />
      </div>

      {/* Conflicts of interest */}
      <div className="soa-section-banner">
        <i className="bi bi-shield-exclamation"></i>
        Conflicts of Interest
      </div>

      <TemplateTextField
        fieldKey="warnings.conflictsDisclosure"
        label="Conflicts of Interest Disclosure"
        value={data.conflictsDisclosure || ''}
        onChange={(val) => handleChange('conflictsDisclosure', val)}
        placeholder="e.g. The adviser may receive commissions or benefits from product providers recommended in this SOA. Details of any conflicts are disclosed here..."
        rows={3}
        disabled={disabled}
        hint="ASIC RG 175 requires disclosure of any conflicts of interest"
      />

      <div className="soa-form-group">
        <label>Adviser Remuneration and Benefits</label>
        <textarea
          className="form-control"
          value={data.adviserRemuneration || ''}
          onChange={(e) => handleChange('adviserRemuneration', e.target.value)}
          placeholder="e.g. The adviser receives a salary from the licensee. The adviser does not receive any commissions from this recommendation. Any soft-dollar benefits are disclosed..."
          rows={3}
        />
      </div>

      {/* Alternatives considered */}
      <div className="soa-section-banner">
        <i className="bi bi-diagram-3"></i>
        Alternatives Considered
      </div>

      <TemplateTextField
        fieldKey="warnings.alternativesConsidered"
        label="What alternatives were considered and why were they not recommended?"
        value={data.alternativesConsidered}
        onChange={(val) => handleChange('alternativesConsidered', val)}
        placeholder="e.g. We considered a more conservative portfolio but it would not meet the client's stated income requirements. Direct share portfolio was also considered but the client's investment amount does not provide sufficient diversification..."
        rows={4}
        disabled={disabled}
        hint="ASIC RG 175 requires that alternatives are documented"
      />

      {/* Custom disclaimer */}
      <div className="soa-form-group">
        <label>Custom Disclaimer (optional)</label>
        <textarea
          className="form-control"
          value={data.customDisclaimer}
          onChange={(e) => handleChange('customDisclaimer', e.target.value)}
          placeholder="Any additional disclaimer text to include in the document..."
          rows={3}
        />
      </div>

      {/* Next steps */}
      <div className="soa-section-banner">
        <i className="bi bi-signpost-2"></i>
        Next Steps
      </div>

      <div className="soa-form-group">
        <label>Next Steps (optional — defaults are included if left blank)</label>
        <textarea
          className="form-control"
          value={data.nextSteps}
          onChange={(e) => handleChange('nextSteps', e.target.value)}
          placeholder="Custom next steps for the client, or leave blank for standard text..."
          rows={3}
        />
      </div>

      {/* Adviser details */}
      <div className="soa-section-banner">
        <i className="bi bi-person-badge"></i>
        Adviser Details
      </div>

      <div className="d-flex gap-2 mb-2">
        {adviserProfile && (adviserProfile.adviser_name || adviserProfile.adviser_email) && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={onUseAdviserProfile}
            disabled={disabled}
          >
            <i className="bi bi-person-check me-1"></i>Use my details
          </button>
        )}
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={handleSaveProfile}
          disabled={disabled || savingProfile || !data.adviserName?.trim()}
        >
          {savingProfile ? (
            <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</>
          ) : (
            <><i className="bi bi-save me-1"></i>Save as my details</>
          )}
        </button>
      </div>

      <div className="row">
        <div className="col-md-6 soa-form-group">
          <label>Adviser Name</label>
          <input type="text" className="form-control" value={data.adviserName} onChange={(e) => handleChange('adviserName', e.target.value)} />
        </div>
        <div className="col-md-6 soa-form-group">
          <label>Adviser Email</label>
          <input type="email" className="form-control" value={data.adviserEmail} onChange={(e) => handleChange('adviserEmail', e.target.value)} />
        </div>
      </div>

      <div className="row">
        <div className="col-md-4 soa-form-group">
          <label>Adviser Phone</label>
          <input type="tel" className="form-control" value={data.adviserPhone} onChange={(e) => handleChange('adviserPhone', e.target.value)} />
        </div>
        <div className="col-md-4 soa-form-group">
          <label>AFSL Number</label>
          <input type="text" className="form-control" value={data.afslNumber} onChange={(e) => handleChange('afslNumber', e.target.value)} />
        </div>
        <div className="col-md-4 soa-form-group">
          <label>Authorised Rep Number</label>
          <input type="text" className="form-control" value={data.authorisedRepNumber} onChange={(e) => handleChange('authorisedRepNumber', e.target.value)} />
        </div>
      </div>

      <div className="soa-step-nav">
        <button className="btn btn-outline-secondary" onClick={prevStep}>
          <i className="bi bi-arrow-left me-1"></i>Back
        </button>
        <div className="text-muted small d-flex align-items-center">
          <i className="bi bi-check-circle me-1"></i>
          All steps complete — use the sidebar to save, preview, or export.
        </div>
      </div>
    </fieldset>
  );
};

export default StepWarnings;
